import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Ticket, User } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { MenuOverlay } from "./MenuOverlay";
import { HashLink } from "./HashLink";

export const Nav = () => {
  const { lang, setLang, t } = useLang();
  const { isAuth, account } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-[80] transition-colors duration-500 ${
          open
            ? "bg-transparent border-b border-transparent"
            : scrolled
            ? "bg-black/85 backdrop-blur-md border-b border-white/5"
            : "bg-gradient-to-b from-black/60 to-transparent border-b border-transparent"
        }`}
      >
        <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3 px-4 sm:px-6 md:px-14 py-4">
          <button
            data-testid="menu-toggle-btn"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            aria-label={open ? t.menu.close : t.menu.open}
            className="flex items-center gap-3 text-white justify-self-start group"
          >
            <span className="relative w-5 h-[11px] block">
              <span
                className={`absolute left-0 top-0 w-full h-[1.5px] bg-current transition-transform duration-500 ${
                  open ? "translate-y-[4.75px] rotate-45" : ""
                }`}
              />
              <span
                className={`absolute left-0 bottom-0 w-full h-[1.5px] bg-current transition-transform duration-500 ${
                  open ? "-translate-y-[4.75px] -rotate-45" : ""
                }`}
              />
            </span>
            <span className={`roll-wrap hidden sm:inline-block text-[0.7rem] uppercase tracking-[0.3em] ${open ? "rolled" : ""}`}>
              <span className="roll-a">{t.menu.open}</span>
              <span className="roll-b" aria-hidden="true">{t.menu.close}</span>
              <span className="block h-0 overflow-hidden opacity-0" aria-hidden="true">{t.menu.close}</span>
            </span>
          </button>

          <HashLink
            id="/"
            data-testid="nav-logo-link"
            className={`font-serif tracking-wide text-white justify-self-center whitespace-nowrap transition-all duration-500 ${
              open ? "text-2xl sm:text-3xl md:text-[2.6rem] leading-none" : "text-base sm:text-lg md:text-2xl"
            } ${
              location.pathname === "/" && !scrolled && !open ? "opacity-0 pointer-events-none" : "opacity-100"
            }`}
          >
            APEX <em className="italic text-[#C5A059]">Ride</em>
          </HashLink>

          <div className="flex items-center gap-3 sm:gap-5 md:gap-7 justify-self-end">
            <div className="hidden sm:flex items-center gap-1.5 text-[0.7rem] tracking-[0.2em]" data-testid="language-toggle">
              <button
                data-testid="lang-en-btn"
                onClick={() => setLang("en")}
                className={`transition-colors duration-300 ${lang === "en" ? "text-[#C5A059]" : "text-zinc-400 hover:text-white"}`}
              >
                EN
              </button>
              <span className="text-zinc-600">/</span>
              <button
                data-testid="lang-es-btn"
                onClick={() => setLang("es")}
                className={`transition-colors duration-300 ${lang === "es" ? "text-[#C5A059]" : "text-zinc-400 hover:text-white"}`}
              >
                ES
              </button>
            </div>
            <Link
              to="/account"
              data-testid="nav-account-link"
              aria-label={t.nav.account}
              className="flex items-center gap-2 text-zinc-300 hover:text-[#C5A059] transition-colors duration-300"
            >
              <User size={15} className="shrink-0" />
              {isAuth && account?.profile?.name && (
                <span className="hidden md:inline text-[0.7rem] tracking-[0.15em] max-w-[7rem] truncate">
                  {account.profile.name.split(" ")[0]}
                </span>
              )}
            </Link>

            <Link to="/book" data-testid="nav-reserve-btn" className="vero-btn !px-5 md:!px-6 !py-2 !text-[0.65rem] flex items-center gap-2">
              <span className="vero-btn-wrap">
                <span className="vero-btn-text">{t.nav.reserve}</span>
                <span className="vero-btn-copy" aria-hidden="true">{t.nav.reserve}</span>
              </span>
              <Ticket size={13} className="shrink-0" />
            </Link>
          </div>
        </div>
      </header>
      <MenuOverlay open={open} onClose={() => setOpen(false)} />
    </>
  );
};
